import React, { type MouseEvent, useCallback, useRef } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/utils";

import type { AccessibleDialogProps } from "./types";
import useDocumentScrollLock from "./useDocumentScrollLock";
import useEscapeKey from "./useEscapeKey";
import useFocusTrap from "./useFocusTrap";

const DEFAULT_TEST_ID = "AccessibleDialog";
const BACKDROP_CLASS_NAME = "fixed inset-0 z-[900000] flex items-center justify-center bg-black/30";
const PANEL_CLASS_NAME = "relative max-h-[90vh] max-w-[90vw] overflow-y-auto outline-none";

const AccessibleDialog = ({
  isOpen,
  onClose,
  ariaLabel,
  labelledBy,
  className,
  backdropClassName,
  children,
  "data-testid": testId = DEFAULT_TEST_ID,
}: AccessibleDialogProps) => {
  const refPanel = useRef<HTMLDivElement>(null);
  const refIsPressOnBackdrop = useRef(false);

  useDocumentScrollLock({ isLocked: isOpen });

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      event.preventDefault();
      onClose();
    },
    [onClose],
  );
  useEscapeKey({ isActive: isOpen, onEscape: handleEscape });

  const handlePanelKeyDown = useFocusTrap({ isActive: isOpen, containerRef: refPanel });

  // A press that starts inside the panel and ends on the backdrop must not close the dialog.
  const handleBackdropMouseDown = useCallback((event: MouseEvent<HTMLDivElement>) => {
    refIsPressOnBackdrop.current = event.target === event.currentTarget;
  }, []);

  const handleBackdropClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (refIsPressOnBackdrop.current && event.target === event.currentTarget) {
        onClose();
      }
      refIsPressOnBackdrop.current = false;
    },
    [onClose],
  );

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      data-testid={testId}
      className={cn(BACKDROP_CLASS_NAME, backdropClassName)}
      onMouseDown={handleBackdropMouseDown}
      onClick={handleBackdropClick}
    >
      <div
        ref={refPanel}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        data-testid={`${testId}-Panel`}
        className={cn(PANEL_CLASS_NAME, className)}
        onKeyDown={handlePanelKeyDown}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

export default React.memo(AccessibleDialog);
